import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  FloppyDisk,
  Buildings,
  ShieldCheck,
} from '@phosphor-icons/react';
import { useAuthStore } from '@dukene/shared';
import { useCreateSupplier, type CreateSupplierDto } from '../../hooks/useSuppliers';

export default function SupplierForm() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const storeId = user?.storeId || (user as any)?.ownedStores?.[0]?.id || '';
  const createSupplier = useCreateSupplier();

  const [formData, setFormData] = useState<CreateSupplierDto>({
    storeId,
    name: '',
    phoneNumber: '',
    email: '',
    company: '',
    address: '',
    city: '',
    country: 'Kenya',
    postalCode: '',
    canViewOrders: true,
    canFulfillOrders: true,
    canViewInventory: false,
    canViewProducts: true,
    notes: '',
    tags: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (storeId && formData.storeId !== storeId) {
      setFormData((prev) => ({ ...prev, storeId }));
    }
  }, [storeId]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Supplier name is required';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    if (!formData.storeId) {
      newErrors.storeId = 'Store ID is missing. Please refresh and try again.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      await createSupplier.mutateAsync(formData);
      navigate('/suppliers');
    } catch (error: any) {
      console.error('Failed to create supplier:', error);
      alert(error.response?.data?.error || 'Failed to create supplier');
    }
  };

  const updateField = (field: keyof CreateSupplierDto, value: any) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Polaris High-Density Header */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/suppliers')}
            className="p-1.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-[#5C5F62] transition-colors"
          >
            <ArrowLeft weight="bold" className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-base font-bold text-[#202223] dark:text-white">
              Add New Supplier
            </h1>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
              Register a direct supplier or manufacturer to manage purchase orders and inventory
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/suppliers')}
            className="h-8 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 text-xs font-semibold text-[#5C5F62] hover:bg-[#F6F6F7]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createSupplier.isPending}
            className="h-8 px-4 bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1.5 shadow-xs transition-colors"
          >
            <FloppyDisk weight="bold" className="w-3.5 h-3.5" />
            {createSupplier.isPending ? 'Creating...' : 'Save Supplier'}
          </button>
        </div>
      </div>

      {/* Main Form Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left Columns: General & Contact Info */}
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm space-y-4">
            <div className="flex items-center justify-between border-b border-[#E1E4E8] dark:border-slate-800 pb-3">
              <h2 className="text-sm font-semibold text-[#202223] dark:text-white">
                Company & Contact Profile
              </h2>
              <Buildings weight="fill" className="w-4 h-4 text-[#5C5F62]" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] mb-1">
                  Supplier / Company Name *
                </label>
                <input
                  type="text"
                  placeholder="e.g. Acme Manufacturing Ltd."
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  className={`w-full h-8 px-2.5 rounded-lg border text-xs bg-white dark:bg-slate-800 text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838] ${
                    errors.name ? 'border-red-500' : 'border-[#D2D5D9] dark:border-slate-700'
                  }`}
                />
                {errors.name && <p className="text-[11px] text-red-600 mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] mb-1">
                  Primary Phone Number *
                </label>
                <input
                  type="text"
                  placeholder="+254 700 000 000"
                  value={formData.phoneNumber}
                  onChange={(e) => updateField('phoneNumber', e.target.value)}
                  className={`w-full h-8 px-2.5 rounded-lg border text-xs bg-white dark:bg-slate-800 text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838] ${
                    errors.phoneNumber ? 'border-red-500' : 'border-[#D2D5D9] dark:border-slate-700'
                  }`}
                />
                {errors.phoneNumber && (
                  <p className="text-[11px] text-red-600 mt-1">{errors.phoneNumber}</p>
                )}
              </div>

              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  placeholder="orders@supplier.com"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className="w-full h-8 px-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
                />
                {errors.email && <p className="text-[11px] text-red-600 mt-1">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] mb-1">
                  City / Location
                </label>
                <input
                  type="text"
                  placeholder="Nairobi"
                  value={formData.city}
                  onChange={(e) => updateField('city', e.target.value)}
                  className="w-full h-8 px-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] mb-1">
                  Physical Address
                </label>
                <input
                  type="text"
                  placeholder="Industrial Area, Road C, Warehouse 4"
                  value={formData.address}
                  onChange={(e) => updateField('address', e.target.value)}
                  className="w-full h-8 px-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] mb-1">
                  Internal Notes
                </label>
                <textarea
                  rows={3}
                  placeholder="Add any credit terms, delivery lead times, or preferred dispatch days..."
                  value={formData.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                  className="w-full p-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Permissions */}
        <div className="space-y-5">
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm space-y-4">
            <div className="flex items-center justify-between border-b border-[#E1E4E8] dark:border-slate-800 pb-3">
              <h2 className="text-sm font-semibold text-[#202223] dark:text-white">
                Supplier Access Rights
              </h2>
              <ShieldCheck weight="fill" className="w-4 h-4 text-[#006838]" />
            </div>

            <div className="space-y-3 text-xs">
              <label className="flex items-center justify-between p-2.5 rounded-lg bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800">
                <span className="font-medium text-[#202223] dark:text-white">
                  Can View Store Orders
                </span>
                <input
                  type="checkbox"
                  checked={formData.canViewOrders}
                  onChange={(e) => updateField('canViewOrders', e.target.checked)}
                  className="rounded border-[#D2D5D9] text-[#006838] focus:ring-[#006838]"
                />
              </label>

              <label className="flex items-center justify-between p-2.5 rounded-lg bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800">
                <span className="font-medium text-[#202223] dark:text-white">
                  Can Fulfill / Dispatch Orders
                </span>
                <input
                  type="checkbox"
                  checked={formData.canFulfillOrders}
                  onChange={(e) => updateField('canFulfillOrders', e.target.checked)}
                  className="rounded border-[#D2D5D9] text-[#006838] focus:ring-[#006838]"
                />
              </label>

              <label className="flex items-center justify-between p-2.5 rounded-lg bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800">
                <span className="font-medium text-[#202223] dark:text-white">
                  Can Monitor Inventory
                </span>
                <input
                  type="checkbox"
                  checked={formData.canViewInventory}
                  onChange={(e) => updateField('canViewInventory', e.target.checked)}
                  className="rounded border-[#D2D5D9] text-[#006838] focus:ring-[#006838]"
                />
              </label>

              <label className="flex items-center justify-between p-2.5 rounded-lg bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800">
                <span className="font-medium text-[#202223] dark:text-white">
                  Can View Products Catalog
                </span>
                <input
                  type="checkbox"
                  checked={formData.canViewProducts}
                  onChange={(e) => updateField('canViewProducts', e.target.checked)}
                  className="rounded border-[#D2D5D9] text-[#006838] focus:ring-[#006838]"
                />
              </label>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
